import React, { useState, useEffect, useCallback } from "react";
import { Container, Form, Button, Spinner } from "react-bootstrap";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import ScaleLoader from "react-spinners/ScaleLoader";
import "../../Styles/yellocalfcss/SMSDeliveryReport.css";

/**
 * SMS Delivery Report
 * --------------------------------------------------------------------------
 * Lists every SMS that was sent (single or bulk) along with its current
 * delivery status — Delivered, Failed, Pending, Phone Off, etc. — so staff
 * can follow up with parents whose messages didn't reach.
 *
 * Backend dependency: this page calls ApiConfig.smsDeliveryReport. The API
 * is expected to return:
 *   { result: true, data: [
 *       { sms_id, sent_date, parent_name, student_name, phone, message,
 *         status, batch_id, error_reason }
 *     ] }
 * If the endpoint is not yet implemented the page renders an empty state.
 */
const SMS_Delivery_Report = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    status: "",
    search: "",
  });

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        customer_id: Number(localStorage.getItem("customer_id")) || 1005,
        api_key: localStorage.getItem("api_key"),
        user_id: Number(localStorage.getItem("user_id")),
        language: "en",
        start_date: filters.startDate,
        end_date: filters.endDate,
        status: filters.status,
        search: filters.search,
      };
      const res = await simplePostCall(
        ApiConfig.smsDeliveryReport,
        JSON.stringify(payload),
      );
      if (res?.result && Array.isArray(res.data)) {
        setRows(res.data);
      } else {
        setRows([]);
      }
    } catch (err) {
      console.error("SMS delivery report fetch failed:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleSearch = () => fetchReport();

  const handleClear = () => {
    setFilters({ startDate: "", endDate: "", status: "", search: "" });
    setTimeout(fetchReport, 0);
  };

  // Map raw status strings (any casing/format) to a normalized key for the
  // colored pill. Anything we don't recognise falls back to "pending".
  const normalizeStatus = (raw) => {
    const s = String(raw || "").toLowerCase().replace(/[^a-z]/g, "");
    if (s.includes("deliver")) return "delivered";
    if (s.includes("fail") || s.includes("error") || s.includes("undeliver"))
      return "failed";
    if (s.includes("phoneoff") || s.includes("offline") || s.includes("dnd"))
      return "phoneoff";
    if (s.includes("send") || s.includes("queue") || s.includes("pending"))
      return "pending";
    return "pending";
  };

  const statusLabel = (key) =>
    ({ delivered: "Delivered", failed: "Failed", phoneoff: "Phone Off", pending: "Pending" }[key] || "Pending");

  // Quick aggregate counts for the summary tiles at the top of the page.
  const counts = rows.reduce(
    (acc, r) => {
      const key = normalizeStatus(r.status);
      acc[key] = (acc[key] || 0) + 1;
      acc.total += 1;
      return acc;
    },
    { total: 0, delivered: 0, failed: 0, phoneoff: 0, pending: 0 },
  );

  return (
    <div className="sms-report-page">
      <Container fluid>
        <div className="special-header panel-white-all">
          <h4 className="page-title">SMS Delivery Report</h4>
        </div>

        {/* Summary tiles */}
        <div className="sms-report-summary-row">
          <div className="sms-report-tile sms-tile-total">
            <span className="sms-report-tile-label">Total Sent</span>
            <span className="sms-report-tile-value">{counts.total}</span>
          </div>
          <div className="sms-report-tile sms-tile-delivered">
            <span className="sms-report-tile-label">Delivered</span>
            <span className="sms-report-tile-value">{counts.delivered}</span>
          </div>
          <div className="sms-report-tile sms-tile-failed">
            <span className="sms-report-tile-label">Failed</span>
            <span className="sms-report-tile-value">{counts.failed}</span>
          </div>
          <div className="sms-report-tile sms-tile-phoneoff">
            <span className="sms-report-tile-label">Phone Off</span>
            <span className="sms-report-tile-value">{counts.phoneoff}</span>
          </div>
          <div className="sms-report-tile sms-tile-pending">
            <span className="sms-report-tile-label">Pending</span>
            <span className="sms-report-tile-value">{counts.pending}</span>
          </div>
        </div>

        {/* Filters */}
        <div className="sms-report-filter-bar">
          <Form.Control
            type="date"
            value={filters.startDate}
            title="Start Date"
            onChange={(e) =>
              setFilters({ ...filters, startDate: e.target.value })
            }
          />
          <Form.Control
            type="date"
            value={filters.endDate}
            title="End Date"
            onChange={(e) =>
              setFilters({ ...filters, endDate: e.target.value })
            }
          />
          <Form.Select
            value={filters.status}
            onChange={(e) =>
              setFilters({ ...filters, status: e.target.value })
            }
          >
            <option value="">All Statuses</option>
            <option value="delivered">Delivered</option>
            <option value="failed">Failed</option>
            <option value="phoneoff">Phone Off</option>
            <option value="pending">Pending</option>
          </Form.Select>
          <Form.Control
            type="text"
            placeholder="Search by parent, student, or phone"
            value={filters.search}
            onChange={(e) =>
              setFilters({ ...filters, search: e.target.value })
            }
          />
          <Button
            onClick={handleSearch}
            disabled={loading}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            {loading ? "Searching..." : "Search"}
          </Button>
          <Button
            onClick={handleClear}
            variant="secondary"
          >
            Clear
          </Button>
        </div>

        {/* Table */}
        <div className="sms-report-table-wrap">
          <table className="sms-report-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Sent On</th>
                <th>Parent</th>
                <th>Student</th>
                <th>Phone</th>
                <th>Message</th>
                <th>Status</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    <ScaleLoader color="#F4BB44" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4 text-muted">
                    No SMS records found.
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => {
                  const key = normalizeStatus(r.status);
                  return (
                    <tr key={r.sms_id || idx}>
                      <td>{idx + 1}</td>
                      <td>{r.sent_date || r.sms_sent_date || "—"}</td>
                      <td>{r.parent_name || r.guardian_name || "—"}</td>
                      <td>{r.student_name || "—"}</td>
                      <td>{r.phone || r.parent_phone || "—"}</td>
                      <td className="sms-report-message-cell">
                        {r.message || "—"}
                      </td>
                      <td>
                        <span className={`sms-status-pill sms-status-${key}`}>
                          {statusLabel(key)}
                        </span>
                      </td>
                      <td>{r.error_reason || r.reason || "—"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Container>
    </div>
  );
};

export default SMS_Delivery_Report;
